import type { Language } from '@eformulieren/engine'
import type { Person, Thread } from '@eformulieren/model'
import { type ReactNode, useRef, useState } from 'react'
import { CountBadge, countName } from '../shell/CountBadge.js'
import { inLanguage } from '../language/text.js'
import { ThreadCard } from './ThreadCard.js'

/**
 * Het paneel Opmerkingen: alle draadjes van het formulier, per sectie (R-73, R-139, CitatenPOC
 * `CommentsPanel.tsx`, ontwerp A van het invulscherm).
 *
 * **De enige plek voor het gesprek** (besluit 12.41 van 1 november 2026). Een opmerking hangt aan een
 * stuk tekst, zoals in de POC; ze begint in de dialoog Opmerking plaatsen (`CommentDialog.tsx`) en het
 * gesprek staat hier, met reageren en oplossen in elke kaart. Onder een veld klapt niets meer open.
 *
 * **Per sectie in formuliervolgorde, en binnen een sectie open eerst.** De POC groepeert per veld; hier
 * is de sectie de eenheid van het formulier (R-01), en bij elk draadje staat de plek waar het hangt als
 * knop ernaartoe: het veld met het citaat in de kaart, en de sprong licht het stuk tekst even op. Een
 * draadje van vóór 1 november 2026 bij een veld of een sectie als geheel blijft staan, met "Bij het
 * veld" of "Bij de sectie"; reageren en oplossen kan daar zoals bij elk ander draadje.
 *
 * **Een filter Open, Opgelost en Alles**, dat de POC niet heeft (opdracht van 31 oktober 2026). Het
 * paneel opent op Open, want dat is wat in deze beurt nog afgehandeld moet worden: een open draadje
 * reist niet mee over een beurtwisseling (ontwerp 4.6).
 *
 * **Reageren en oplossen zijn dezelfde handelingen als bij het veld** (`ThreadCard`, de regels van
 * `commentWay` in `FillForm`): wie schrijft, schrijft in het document; een meelezer reageert langs de
 * pod en doet niets af. Een eigen opmerking verwijderen kan in de kaart, na een bevestiging (eisenboek
 * 12.45): wie schrijft, in het document; een meelezer langs de pod. Verdwijnt daarmee het draadje, dan
 * gaat de focus naar het paneel zelf en zegt een melding wat er gebeurde (WCAG 2.4.3, 4.1.3).
 */

export interface OverviewThread {
  readonly draad: Thread
  /** De plek als tekst: "Samenvatting", of "de sectie Inleiding". */
  readonly plek: string
  /** Staat het anker van een draadje met citaat nog in de tekst? */
  readonly verankerd: boolean
  readonly bewerkbaar: boolean
  readonly kanReageren: boolean
}

export interface OverviewGroup {
  readonly sectieId: string
  readonly titel: string
  readonly draden: readonly OverviewThread[]
}

export type CommentFilter = 'open' | 'afgedaan' | 'alles'

export interface CommentsOverviewProps {
  readonly taal: Language
  readonly groepen: readonly OverviewGroup[]
  readonly onSpring: (sectieId: string, thread: Thread) => void
  readonly onAntwoord: (sectieId: string, threadId: string, text: string) => unknown
  readonly onAfdoen: (sectieId: string, threadId: string, settled: boolean) => void
  readonly naamVan?: (person: string) => string
  /** Of de lezer zelf een opmerking kan plaatsen; dan staat bij een leeg paneel hoe dat gaat. */
  readonly kanPlaatsen?: boolean
  /** Wie dit leest, zoals de pod hem stempelt; zonder dit geen verwijderknop (eisenboek 12.45). */
  readonly ik?: Person
  /** Een eigen bericht verwijderen, of zonder `messageId` het hele draadje; een belofte als het langs de pod gaat. */
  readonly onVerwijder?: (sectieId: string, threadId: string, messageId: string | undefined) => unknown
}

const FILTER_LABEL = { nl: 'Welke opmerkingen', en: 'Which comments' }
/** Het label van elk filter; het aantal staat ernaast als vak (`CountBadge`) en in de naam ("Open: 2"). */
const FILTERS: Readonly<Record<CommentFilter, { nl: string; en: string }>> = {
  open: { nl: 'Open', en: 'Open' },
  afgedaan: { nl: 'Opgelost', en: 'Resolved' },
  alles: { nl: 'Alles', en: 'All' },
}
const EMPTY: Readonly<Record<CommentFilter, { nl: string; en: string }>> = {
  open: { nl: 'Er staat geen opmerking open.', en: 'There is no open comment.' },
  afgedaan: { nl: 'Er is nog geen opmerking opgelost.', en: 'No comment has been resolved yet.' },
  alles: { nl: 'Nog geen opmerkingen', en: 'No comments yet' },
}
const HOW = {
  nl: 'Een opmerking plaatst u bij een stuk tekst: selecteer de tekst in een tekstveld en kies Opmerking bij de selectie in de balk erboven.',
  en: 'You place a comment on a piece of text: select the text in a text field and choose Comment on the selection in the bar above it.',
}
const AT = (place: string) => ({ nl: `Bij ${place}`, en: `On ${place}` })
const DELETED_MESSAGE = { nl: 'Uw opmerking is verwijderd.', en: 'Your comment has been deleted.' }
const DELETED_THREAD = { nl: 'Het draadje is verwijderd.', en: 'The thread has been deleted.' }

function passes(thread: Thread, filter: CommentFilter): boolean {
  if (filter === 'alles') return true
  return filter === 'open' ? !thread.opgelost : thread.opgelost
}

export function CommentsOverview({ taal: language, groepen: groups, onSpring: onJump, onAntwoord: onReply, onAfdoen: onSettle, naamVan: nameOf, kanPlaatsen: canPlace = false, ik: me, onVerwijder: onDelete }: CommentsOverviewProps): ReactNode {
  const [filter, setFilter] = useState<CommentFilter>('open')
  const [notice, setNotice] = useState('')
  const overview = useRef<HTMLDivElement>(null)
  const all = groups.flatMap((group) => group.draden)
  const counts: Readonly<Record<CommentFilter, number>> = {
    open: all.filter((entry) => !entry.draad.opgelost).length,
    afgedaan: all.filter((entry) => entry.draad.opgelost).length,
    alles: all.length,
  }
  const shown = groups
    .map((group) => ({
      ...group,
      draden: [...group.draden]
        .filter((entry) => passes(entry.draad, filter))
        // Open eerst, dan op het moment van het eerste bericht, oudste eerst.
        .sort((a, b) => Number(a.draad.opgelost) - Number(b.draad.opgelost) || (a.draad.berichten[0]?.moment ?? '').localeCompare(b.draad.berichten[0]?.moment ?? '')),
    }))
    .filter((group) => group.draden.length > 0)

  return (
    // Focusbaar met tabIndex -1: na het verwijderen van een draadje is de kaart weg, en dan gaat de focus hierheen.
    <div ref={overview} className="ef-opmerkingoverzicht" tabIndex={-1}>
      <p className="ef-sr-only" role="status">
        {notice}
      </p>
      {all.length === 0 ? null : (
        <div className="ef-advies__filter" role="group" aria-label={inLanguage(FILTER_LABEL, language)}>
          {(['open', 'afgedaan', 'alles'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="ef-advies__filterknop"
              aria-pressed={filter === option}
              aria-label={countName(inLanguage(FILTERS[option], language), [String(counts[option])])}
              onClick={() => setFilter(option)}
            >
              {inLanguage(FILTERS[option], language)}
              <CountBadge>{String(counts[option])}</CountBadge>
            </button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <>
          <p className="ef-opmerkingoverzicht__leeg">{inLanguage(all.length === 0 ? EMPTY.alles : EMPTY[filter], language)}</p>
          {all.length === 0 && canPlace ? <p className="ef-opmerkingoverzicht__hulp">{inLanguage(HOW, language)}</p> : null}
        </>
      ) : (
        shown.map((group) => (
          <section key={group.sectieId} className="ef-opmerkingoverzicht__sectie" aria-labelledby={`ef-opmerkingen-${group.sectieId}`}>
            <h3 className="ef-opmerkingoverzicht__kop" id={`ef-opmerkingen-${group.sectieId}`}>
              {group.titel}
            </h3>
            <ol className="ef-draden__lijst ef-opmerkingoverzicht__lijst">
              {group.draden.map((entry) => (
                <ThreadCard
                  key={entry.draad.id}
                  draad={entry.draad}
                  taal={language}
                  anchored={entry.draad.citaat === '' || entry.verankerd}
                  bewerkbaar={entry.bewerkbaar}
                  kanReageren={entry.kanReageren}
                  onAntwoord={(threadId, text) => onReply(group.sectieId, threadId, text)}
                  onAfdoen={(threadId, settled) => onSettle(group.sectieId, threadId, settled)}
                  onJump={() => onJump(group.sectieId, entry.draad)}
                  {...(nameOf === undefined ? {} : { naamVan: nameOf })}
                  {...(me === undefined || onDelete === undefined
                    ? {}
                    : {
                        ik: me,
                        onVerwijder: (threadId: string, messageId: string | undefined) => onDelete(group.sectieId, threadId, messageId),
                        onVerwijderd: (threadGone: boolean) => {
                          setNotice(inLanguage(threadGone ? DELETED_THREAD : DELETED_MESSAGE, language))
                          if (threadGone) requestAnimationFrame(() => overview.current?.focus())
                        },
                      })}
                  kopregel={
                    <p className="ef-opmerkingoverzicht__plek">
                      <button type="button" className="ef-opmerkingoverzicht__plekknop" onClick={() => onJump(group.sectieId, entry.draad)}>
                        {inLanguage(AT(entry.plek), language)}
                      </button>
                    </p>
                  }
                />
              ))}
            </ol>
          </section>
        ))
      )}
    </div>
  )
}
